import {
  DepositPaymentTargetAccount,
  NbSelectItemOption,
  depositInternalCurrencyListDefault,
  isDefined,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { CashActionStep } from '../states/cashActionAtoms';
import { internalDepositSelectedCurrencyAtom, selectedTargetAccountAtom } from '../states/depositAtoms';
import { useDeposit } from './useDeposit';
import { usePayment } from './usePayment';

export const useDepositInternal = () => {
  const {
    submitDepositPayment,
    confirmDepositPayment,
    setTempId,
    tempId,
    handleTwoFactorError,
    smsCodeError,
    setSmsCodeError,
    canRestartTwoFactor,
    targetAccounts,
    cashActionStep,
    cashActionTemp,
    setCashActionStep,
    setCashActionTemp,
    getSubmitErrors,
    setReceivedMessages,
  } = usePayment();
  const { refreshDepositDetails, depositDetails, getTargetAccounts } = useDeposit();
  const { translation } = useLocale();
  const [selectedTargetAccount, setSelectedTargetAccount] = useAtom(selectedTargetAccountAtom);
  const [selectedInternalCurrency, setSelectedInternalCurrency] = useAtom(internalDepositSelectedCurrencyAtom);

  const selectedInternalDetails = useMemo(() => {
    return depositDetails.isLoaded
      ? depositDetails.depositPaymentOptions.find(d => d.currencyCode === selectedInternalCurrency)
      : undefined;
  }, [depositDetails, selectedInternalCurrency]);

  const currencyOptions = useMemo<Array<NbSelectItemOption<string>>>(
    () =>
      depositInternalCurrencyListDefault.map(c => ({
        id: c,
        name: translation.portfolio.currencies[c as keyof typeof translation.portfolio.currencies] ?? c,
      })),
    [translation],
  );

  const handleTargetAccountChange = useCallback(
    (value: string | DepositPaymentTargetAccount) => {
      if (typeof value === 'string') {
        setSelectedTargetAccount(undefined);
        return;
      }

      setSelectedTargetAccount(value);
    },
    [setSelectedTargetAccount],
  );

  const handleDepositSubmit = useCallback(
    async (onConfirm: () => void) => {
      if (
        !isDefined(cashActionTemp) ||
        !isDefined(cashActionTemp.sourceAccountNumber) ||
        !isDefined(cashActionTemp.targetAccountCategory)
      ) {
        return;
      }

      try {
        const resp = await submitDepositPayment({
          submitDepositPaymentRequest: {
            amount: parseFloat(cashActionTemp.amount),
            targetAccountCategory: cashActionTemp.targetAccountCategory,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceAccountNumber: cashActionTemp.sourceAccountNumber,
            sourceClientId: cashActionTemp.sourceClientId,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          if (resp.data.responseType === 'submitted') {
            setTempId(resp.data.tempId);
          }
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'submitted') {
          setTempId(resp.data.tempId);
          setCashActionStep(CashActionStep.Confirm);
        } else {
          onConfirm();
        }
      } catch (error) {
        setReceivedMessages([{ msgType: 'error', msg: translation.cashAction.errorsCommon.cannotSubmit }]);
        setCashActionStep(CashActionStep.ReceivedMessages);
      }
    },
    [cashActionTemp, submitDepositPayment, setTempId, setCashActionStep, setReceivedMessages, translation],
  );

  const handleDepositConfirm = useCallback(
    async (smsCode: string, onSuccess: () => void) => {
      if (!isDefined(cashActionTemp) || !isDefined(tempId) || !isDefined(cashActionTemp.targetAccountCategory)) {
        return;
      }

      try {
        const resp = await confirmDepositPayment({
          tempId,
          confirmDepositPaymentRequest: {
            smsCode,
            amount: parseFloat(cashActionTemp.amount),
            targetAccountCategory: cashActionTemp.targetAccountCategory,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceClientId: cashActionTemp.sourceClientId,
            sourceAccountNumber: cashActionTemp.sourceAccountNumber,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          setTempId(undefined);
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'confirmed') {
          onSuccess();
        }
      } catch (error) {
        handleTwoFactorError(error);
      }
    },
    [
      cashActionTemp,
      confirmDepositPayment,
      tempId,
      handleTwoFactorError,
      setReceivedMessages,
      setCashActionStep,
      setTempId,
    ],
  );

  return {
    getTargetAccounts,
    refreshDepositDetails,
    handleTargetAccountChange,
    setCashActionStep,
    setCashActionTemp,
    handleDepositSubmit,
    handleDepositConfirm,
    setSelectedInternalCurrency,
    canRestartTwoFactor,
    setSmsCodeError,
    getSubmitErrors,
    targetAccounts,
    selectedInternalDetails,
    currencyOptions,
    depositDetails,
    cashActionStep,
    cashActionTemp,
    smsCodeError,
    selectedTargetAccount,
    selectedInternalCurrency,
  };
};
